import React, { lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import { AnalyticsPage } from './analyticsDashboard/components/AnalyticsPage';
import { BrowseResultsPage } from './browse/BrowseResultsPage';
import { BrowseDataProduct } from './browse/BrowseDataProduct';
import { ManageDomainsPage } from './domain/ManageDomainsPage';
import { SearchablePage } from './search/SearchablePage';
import { SettingsPage } from './settings/SettingsPage';
import { RouteInterceptor } from './RouteInterceptor';
import { NoPageFound } from './shared/NoPageFound';
import { PageRoutes } from '../conf/Global';
import GrantedPrivilegesProvider from './entity/shared/GrantedPrivilegesProvider';

import { useIsNestedDomainsEnabled } from './useAppConfig';
import { useEntityRegistry } from './useEntityRegistry';
import GlossaryRoutes from './glossary/GlossaryRoutes';
import { EntityPage } from './entity/EntityPage';
import { SearchPage } from './search/SearchPage';
import { EntityType } from '../types.generated';

const DomainRoutes = lazy(() => import('./domain/DomainRoutes'));

/**
 * Container for all searchable page routes
 */
export const SearchRoutes = (): JSX.Element => {
    const entityRegistry = useEntityRegistry();
    const isNestedDomainsEnabled = useIsNestedDomainsEnabled();
    const entities = isNestedDomainsEnabled
        ? entityRegistry.getEntitiesForSearchRoutes()
        : entityRegistry.getNonGlossaryEntities();

    return (
        <SearchablePage>
            <Switch>
                {entities.map((entity) => (
                    <RouteInterceptor
                        entityType={entity.type}
                        key={entity.getPathName()}
                        path={`/${entity.getPathName()}/:urn`}
                        render={() => <EntityPage entityType={entity.type} />}
                    />
                ))}
                <RouteInterceptor
                    entityType={EntityType.DataProduct}
                    path={PageRoutes.BROWSE_DATAPRODUCT}
                    render={() => <BrowseDataProduct />}
                />
                {isNestedDomainsEnabled && (
                    <RouteInterceptor
                        entityType={EntityType.Domain}
                        path={`${PageRoutes.DOMAIN}*`}
                        render={() => (
                            <Suspense fallback={null}>
                                <DomainRoutes />
                            </Suspense>
                        )}
                    />
                )}
                {!isNestedDomainsEnabled && (
                    <RouteInterceptor
                        entityType={EntityType.Domain}
                        path={PageRoutes.DOMAINS}
                        render={() => <ManageDomainsPage />}
                    />
                )}
                <RouteInterceptor
                    entityType={EntityType.GlossaryTerm}
                    path={PageRoutes.GLOSSARY_TERM_DETAILS}
                    render={() => <GlossaryRoutes />}
                />
                <RouteInterceptor
                    entityType={EntityType.GlossaryNode}
                    path={`${PageRoutes.GLOSSARY}*`}
                    render={() => <GlossaryRoutes />}
                />
                <Route path={PageRoutes.SEARCH_RESULTS} render={() => <SearchPage />} />
                <Route path={PageRoutes.BROWSE_RESULTS} render={() => <BrowseResultsPage />} />
                <Route path={PageRoutes.ANALYTICS} render={() => <AnalyticsPage />} />
                <Route
                    path={PageRoutes.POLICIES}
                    render={() => <Redirect to="/settings/permissions-debug/policies-debug" />}
                />
                <Route
                    path={PageRoutes.SETTINGS_POLICIES}
                    render={() => <Redirect to="/settings/permissions-debug/policies-debug" />}
                />
                <Route path={PageRoutes.PERMISSIONS} render={() => <Redirect to="/settings/permissions-debug" />} />
                <Route path={PageRoutes.IDENTITIES} render={() => <Redirect to="/settings/identities-debug" />} />
                <Route
                    path={PageRoutes.SETTINGS}
                    render={() => (
                        <GrantedPrivilegesProvider>
                            <SettingsPage />
                        </GrantedPrivilegesProvider>
                    )}
                />
                <Route component={NoPageFound} />
            </Switch>
        </SearchablePage>
    );
};
